import React, { useEffect, useState } from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  Card, CardContent, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Button, TextField
} from '@mui/material';
import axiosInstance from '../../api/axiosConfig';

const StudentResults = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [examName, setExamName] = useState('');
  const [examDate, setExamDate] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [marks, setMarks] = useState({});
  const [absent, setAbsent] = useState({});

  useEffect(() => {
    loadStudents();
  }, []);

  const loadStudents = async () => {
    try {
      const res = await axiosInstance.get('/students/search?pageSize=1000');
      const data = res.data.students || res.data.data || res.data;
      setStudents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleStudentChange = async (studentId) => {
    setSelectedStudent(studentId);
    setSelectedResult(null);
    setEditingResult(null);
    setLoading(true);

    try {
      const res = await axiosInstance.get(`/results/student/${studentId}`);
      setResults(res.data);
    } catch (err) {
      console.error(err);
      alert("No results found");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewResult = async (resultId) => {
    try {
      const res = await axiosInstance.get(`/results/${resultId}`);
      setSelectedResult(res.data);
      setEditingResult(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditResult = async (resultId) => {
    try {
      const res = await axiosInstance.get(`/results/${resultId}`);
      const result = res.data;

      setEditingResult(result);
      setExamName(result.examName || '');
      setExamDate(result.examDate ? result.examDate.split('T')[0] : '');

      // Load subjects for the student's course
      // 🔥 Get full student details to obtain courseId
const studentRes = await axiosInstance.get(`/students/${result.studentId}`);
const fullStudent = studentRes.data;

if (!fullStudent.courseId) {
  alert("Student course not found");
  return;
}

const subjectsRes = await axiosInstance.get(`/subjects/by-course/${fullStudent.courseId}`);
setSubjects(subjectsRes.data || []);


      // Populate marks and absent status
      const marksObj = {};
      const absentObj = {};

      if (result.studentResultDetails && Array.isArray(result.studentResultDetails)) {
        result.studentResultDetails.forEach(d => {
          marksObj[d.subjectId] = d.marksObtained || 0;
          absentObj[d.subjectId] = d.isAbsent || false;
        });
      }

      setMarks(marksObj);
      setAbsent(absentObj);
      setSelectedResult(null);
    } catch (err) {
      console.error(err);
      alert('Error loading result for editing');
    }
  };

  const handleSubmitResults = async () => {
    if (!editingResult) return;

    const payload = {
      examName,
      examDate,
      subjectResults: subjects.map(s => ({
        subjectId: s.subjectId,
        marksObtained: parseInt(marks[s.subjectId]) || 0,
        isAbsent: absent[s.subjectId] || false
      }))
    };

    try {
      await axiosInstance.put(`/results/${editingResult.resultId}`, payload);
      alert("Result updated successfully");
      setEditingResult(null);
      setMarks({});
      setAbsent({});
      // Reload results
      handleStudentChange(selectedStudent);
    } catch (err) {
      console.error(err);
      alert("Failed to update result");
    }
  };

  const handleCancelEdit = () => {
    setEditingResult(null);
    setMarks({});
    setAbsent({});
    setExamName('');
    setExamDate('');
  };

  const handleDownloadMarksheet = async (resultId) => {
    try {
      const res = await axiosInstance.get(`/results/${resultId}/marksheet-pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'marksheet.pdf');
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error(err);
      alert('Error downloading marksheet');
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Student Results
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Select Student</InputLabel>
        <Select value={selectedStudent} onChange={(e) => handleStudentChange(e.target.value)}>
          {students.map(s => (
            <MenuItem key={s.studentId} value={s.studentId}>
              {s.fullName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && <CircularProgress />}

      {/* Edit Result Form */}
      {editingResult && (
        <Card sx={{ mb: 4, backgroundColor: '#fff3cd', padding: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Edit Result - {editingResult.examName}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
              <TextField
                label="Exam Name"
                value={examName}
                onChange={(e) => setExamName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Exam Date"
                type="date"
                value={examDate}
                onChange={(e) => setExamDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Subject Marks:
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Subject</TableCell>
                  <TableCell>Max Marks</TableCell>
                  <TableCell>Marks Obtained</TableCell>
                  <TableCell>Absent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjects && subjects.length > 0 ? (
                  subjects.map(s => (
                    <TableRow key={s.subjectId}>
                      <TableCell>{s.subjectName}</TableCell>
                      <TableCell>{s.maxMarks || 100}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={marks[s.subjectId] || ''}
                          onChange={(e) =>
                            setMarks({ ...marks, [s.subjectId]: e.target.value })
                          }
                          disabled={absent[s.subjectId]}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={absent[s.subjectId] || false}
                          onChange={(e) =>
                            setAbsent({ ...absent, [s.subjectId]: e.target.checked })
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No subjects available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button variant="contained" color="success" onClick={handleSubmitResults}>
                Update Result
              </Button>
              <Button variant="outlined" onClick={handleCancelEdit}>
                Cancel
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Exam List */}
      {results.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6">Exam Results</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Exam Name</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Obtained</TableCell>
                  <TableCell>Percentage</TableCell>
                  <TableCell>Grade</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map(r => (
                  <TableRow key={r.resultId}>
                    <TableCell>{r.examName}</TableCell>
                    <TableCell>{r.totalMarks}</TableCell>
                    <TableCell>{r.obtainedMarks}</TableCell>
                    <TableCell>{r.percentage}%</TableCell>
                    <TableCell>{r.grade}</TableCell>
                    <TableCell>{r.resultStatus}</TableCell>
                    <TableCell>
                        <Button size="small" onClick={() => handleEditResult(r.resultId)}>
                          Edit
                        </Button>
                      <Button size="small" onClick={() => handleViewResult(r.resultId)}>
                        View
                      </Button>
                      <Button size="small" onClick={() => handleDownloadMarksheet(r.resultId)}>
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Subject-wise Marksheet */}
      {selectedResult && (
        <Card>
          <CardContent>
            <Typography variant="h6">
              {selectedResult.examName} - Subject Marks
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Subject</TableCell>
                  <TableCell>Marks</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedResult.studentResultDetails && selectedResult.studentResultDetails.length > 0 ? (
                  selectedResult.studentResultDetails.map(d => (
                    <TableRow key={d.resultDetailId}>
                      <TableCell>{d.subject ? d.subject.subjectName : d.subjectName || 'N/A'}</TableCell>
                      <TableCell>{d.marksObtained || 0}</TableCell>
                      <TableCell>{d.isAbsent ? 'Absent' : 'Present'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">No subject details available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default StudentResults;
